import React from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Calendar,
  UserCircle,
  CheckCircle,
  ListChecks
} from '@phosphor-icons/react';
import { Epic, EpicStatus } from 'shared/types/epic';
import { useUsers } from 'shared/contexts/users.context';
import { format, isValid } from 'date-fns';
import { nl } from 'date-fns/locale';

interface EpicCardProps {
  epic: Epic;
  onClick: () => void;
  progress?: {
    total: number;
    completed: number;
    percentComplete: number;
  };
  isHighlighted?: boolean;
}

export const EpicCard: React.FC<EpicCardProps> = ({
  epic,
  onClick,
  progress,
  isHighlighted = false
}) => {
  const { users } = useUsers();

  const getUserName = (userId: string) => {
    const foundUser = users.find((user) => user.id === userId);
    return foundUser
      ? `${foundUser.first_name} ${foundUser.last_name}`
      : 'Onbekend';
  };

  const statusConfig = {
    [EpicStatus.PLANNING]: {
      color: 'text-blue-700',
      bgColor: 'bg-blue-100',
      label: 'Planning'
    },
    [EpicStatus.IN_PROGRESS]: {
      color: 'text-moss-primary',
      bgColor: 'bg-moss-primary/10',
      label: 'In Behandeling'
    },
    [EpicStatus.COMPLETED]: {
      color: 'text-status-good',
      bgColor: 'bg-status-good/10',
      label: 'Afgerond'
    },
    [EpicStatus.ON_HOLD]: {
      color: 'text-orange-700',
      bgColor: 'bg-orange-100',
      label: 'On Hold'
    }
  };

  const config = statusConfig[epic.status];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isValid(date) ? format(date, 'd MMM yyyy', { locale: nl }) : null;
  };

  const startDateFormatted = formatDate(epic.startDate);
  const endDateFormatted = formatDate(epic.endDate);

  // Calculate progress
  const totalFeatures = progress?.total || epic.features?.length || 0;
  const completedFeatures = progress?.completed || 0;
  const percentComplete =
    progress?.percentComplete ||
    (totalFeatures > 0 ? Math.round((completedFeatures / totalFeatures) * 100) : 0);

  return (
    <Card
      className={`cursor-pointer transition-all duration-200 ease-out hover:shadow-xl hover:-translate-y-1 hover:scale-[1.02] active:scale-[0.98] ${
        isHighlighted ? 'ring-2 ring-moss-primary shadow-lg' : ''
      }`}
      onClick={onClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <h3 className="font-semibold text-sm line-clamp-2 flex-1 transition-colors hover:text-moss-primary">
              {epic.title}
            </h3>
          </div>
          <Badge
            className={`${config.bgColor} ${config.color} border-0 text-xs px-2 py-0.5 transition-all hover:scale-105 shrink-0`}
          >
            {config.label}
          </Badge>
        </div>
        <div className="flex items-center gap-2 text-xs mt-1">
          <span className="text-neutral-400 font-mono transition-colors hover:text-moss-primary">
            EPIC-{epic.id}
          </span>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Description */}
        {epic.description && (
          <p className="text-xs text-neutral-600 line-clamp-2">
            {epic.description}
          </p>
        )}

        {/* Date Range */}
        {(startDateFormatted || endDateFormatted) && (
          <div className="flex items-center gap-1 text-xs text-neutral-500">
            <Calendar size={14} className="transition-transform hover:scale-110" />
            <span>
              {startDateFormatted || '?'} - {endDateFormatted || '?'}
            </span>
          </div>
        )}

        {/* Progress */}
        {totalFeatures > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1 text-neutral-600">
                <ListChecks size={14} weight="duotone" />
                <span>
                  {completedFeatures} / {totalFeatures} features
                </span>
              </div>
              <span className="font-semibold text-moss-primary">
                {percentComplete}%
              </span>
            </div>
            <Progress value={percentComplete} className="h-2" />
          </div>
        )}

        {/* Owner */}
        <div className="flex items-center justify-between text-xs text-neutral-500 pt-2 border-t">
          {epic.ownerId ? (
            <div className="flex items-center gap-1 transition-all hover:text-moss-primary">
              <UserCircle size={14} weight="fill" className="text-moss-primary" />
              <span className="text-moss-primary font-medium">
                {getUserName(epic.ownerId)}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <UserCircle size={14} />
              <span>Geen eigenaar</span>
            </div>
          )}

          {epic.status === EpicStatus.COMPLETED && (
            <div className="flex items-center gap-1 text-status-good">
              <CheckCircle size={14} weight="fill" />
              <span className="font-medium">Afgerond</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
